import * as React from 'react';
import styled from 'styled-components';

import { TextButton } from '@creditornot/cb-button';
import { colors } from '@creditornot/cb-ingredients';
import { color } from '@creditornot/cb-ingredients/design-tokens';
import { MenuActionItem, MenuCheckboxItem, MenuItemGroup } from '@creditornot/cb-menu';
import { Spinner } from '@creditornot/cb-progress';
import { Switch } from '@creditornot/cb-switch';
import { getCountryMessageId } from 'common/utils/localizedMessages';

import { CountryCode } from '../../../api-types/common';
import { Country } from '../../../api-types/director';
import { useFormatMessage } from '../../../hooks/useFormatMessage';
import { Flag } from '../Flag';

const Root = styled.div`
  min-width: 160px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  overflow: auto;
  max-height: 300px;
`;

const StyledMenuItemGroup = styled(MenuItemGroup)`
  padding: 8px 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  overflow: auto;
  max-height: 300px;
`;

const StyledMenuActionItem = styled(MenuActionItem)`
  margin: 0;
  margin-bottom: 4px;
`;

const StyledMenuCheckboxItem = styled(MenuCheckboxItem)`
  margin: 0;
  margin-bottom: 4px;
`;

const ItemContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding-right: 2px;
  justify-content: space-between;
`;

const StyledFlag = styled(Flag)`
  box-shadow: 0 0 2px 0 ${colors.pepper32};
`;

const ActionsContainer = styled.div`
  padding: 12px 16px;
  border-top: 1px solid ${color.border};
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

type Props = {
  countries: Country[];
  isError: boolean;
  isLoading: boolean;
  selected: CountryCode[];
  onChange: (selected: CountryCode[]) => void;
  onClose: () => void;
};

export const CountryPopover: React.FC<Props> = ({
  countries,
  selected,
  onChange,
  onClose,
  isLoading,
  isError,
}) => {
  const formatMessage = useFormatMessage();
  const [multiple, setMultiple] = React.useState(selected.length > 1);

  function selectAll() {
    onChange(countries.map((c) => c.countryCode as CountryCode /* TODO api specs */));
  }

  function deselectAll() {
    onChange([]);
  }

  function toggle(countryCode: CountryCode) {
    if (selected.includes(countryCode)) {
      onChange(selected.filter((c) => c !== countryCode));
    } else {
      onChange([...selected, countryCode]);
    }
  }

  function select(countryCode: CountryCode) {
    onChange([countryCode]);
    onClose();
  }

  if (isLoading) {
    return (
      <Container>
        <Spinner size="small" />
      </Container>
    );
  }

  if (isError) {
    return (
      <Container>{formatMessage('components.country_selector.error_loading_countries')}</Container>
    );
  }

  const itemContent = (countryCode: CountryCode, localizedCountry: string) => (
    <ItemContent>
      {localizedCountry}
      <StyledFlag countryCode={countryCode} />
    </ItemContent>
  );

  return (
    <Root data-test-id="select-country-popover">
      <StyledMenuItemGroup role="menu">
        {countries
          .map((country) => {
            const countryCode = country.countryCode as CountryCode; // TODO api specs
            const id = getCountryMessageId(countryCode);
            const localizedCountry = formatMessage(id, undefined, countryCode);
            return { ...country, localizedCountry };
          })
          .sort((a, b) => a.localizedCountry.localeCompare(b.localizedCountry))
          .map(({ countryCode: _countryCode, localizedCountry }) => {
            const countryCode = _countryCode as CountryCode; // TODO api specs

            return multiple ? (
              <StyledMenuCheckboxItem
                key={countryCode}
                checked={selected.includes(countryCode)}
                onClick={() => toggle(countryCode)}
              >
                {itemContent(countryCode, localizedCountry)}
              </StyledMenuCheckboxItem>
            ) : (
              <StyledMenuActionItem
                variant="black"
                key={countryCode}
                selected={selected.includes(countryCode)}
                onClick={() => select(countryCode)}
              >
                {itemContent(countryCode, localizedCountry)}
              </StyledMenuActionItem>
            );
          })}
      </StyledMenuItemGroup>
      <ActionsContainer>
        <Switch
          checked={multiple}
          onChange={() => setMultiple(!multiple)}
          disabled={selected.length > 1}
        >
          {formatMessage('components.country_selector.select_multiple_countries')}
        </Switch>
      </ActionsContainer>
      {multiple && (
        <ActionsContainer>
          <TextButton onClick={selectAll}>{formatMessage('common.select_all')}</TextButton>
          {selected.length > 0 && (
            <TextButton onClick={deselectAll}>{formatMessage('common.deselect_all')}</TextButton>
          )}
        </ActionsContainer>
      )}
    </Root>
  );
};
